/**
 * styleTokens — shared design tokens cho Production Report module.
 *
 * Định hướng style: Modern web dashboard.
 * - 1 primary accent (purple) + 1 success (green) + 1 danger (red);
 *   tất cả phần còn lại dùng neutral grays.
 * - Section header: text + chip, KHÔNG dùng colored bg blocks (Excel-style).
 * - Card: white bg, border subtle, accent stripe bên trái cho KPI khi cần.
 * - Density: padding rộng hơn Excel-style (8-16px), font ~0.78–0.95rem.
 *
 * Khi cần thêm accent (vd biểu thị positive variance) chỉ dùng color text +
 * subtle bg, không tô full bg cell.
 */
import type { SxProps, Theme } from '@mui/material/styles'

// Accent theo tone TLC (theme/index.ts): teal #145a59 / copper #da833c / signal
// red #bc2434 / success #2f8f63. NEUTRAL giữ sáng (white + gray lạnh) như cũ —
// chị Đẹp muốn nền sáng, KHÔNG ngả cream.
export const COLORS = {
  primary: '#145a59',      // TLC teal (primaryMain)
  primarySoft: '#e0eceb',  // teal tint nhạt
  success: '#2f8f63',
  successSoft: '#dcefe4',
  danger: '#bc2434',
  dangerSoft: '#f6dadd',
  warn: '#da833c',         // TLC copper
  warnStrong: '#a8551c',   // copper đậm — dòng nhấn mạnh (strong)
  warnSoft: '#fbe9d8',
  text: '#1f2937',
  textMuted: '#6b7280',
  textSubtle: '#9ca3af',
  border: '#e5e7eb',
  borderStrong: '#d1d5db',
  bgPage: '#ffffff',
  bgHover: '#f9fafb',
  bgHeader: '#f3f4f6',
  bgSubtle: '#f9fafb',
} as const

/** Section card — wrapper Paper với border subtle, không có colored header. */
export const sectionCardSx: SxProps<Theme> = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 1.5,
  bgcolor: COLORS.bgPage,
  overflow: 'hidden',
  boxShadow: 'none',
}

/** Header row trong section card — title text + actions (no colored bg). */
export const sectionHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  py: 1.25,
  borderBottom: `1px solid ${COLORS.border}`,
  bgcolor: COLORS.bgPage,
}

export const sectionTitleSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  fontWeight: 700,
  color: COLORS.text,
  letterSpacing: 0.2,
}

export const sectionSubtitleSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: COLORS.textMuted,
}

/** Table header cell — neutral gray bg, dark text. */
export const tableHeaderCellSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: COLORS.text,
  py: 1,
  px: 1.25,
  bgcolor: COLORS.bgHeader,
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${COLORS.border}`,
  letterSpacing: 0.1,
}

/** Table body cell — base styling, no border accent. */
export const tableBodyCellSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  color: COLORS.text,
  py: 0.875,
  px: 1.25,
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${COLORS.border}`,
}

/** Total row — subtle bg, bold; no colored top border. */
export const tableTotalRowCellSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  fontWeight: 700,
  color: COLORS.text,
  py: 0.875,
  px: 1.25,
  whiteSpace: 'nowrap',
  bgcolor: COLORS.bgSubtle,
  borderTop: `1px solid ${COLORS.borderStrong}`,
  borderBottom: 'none',
}

/** Sticky header (top + left) — match neutral header bg. */
export const stickyHeaderBg = COLORS.bgHeader
export const stickyBodyBg = COLORS.bgPage

/** Variance color helper — green for positive, red for negative, gray for zero. */
export function varianceColor(v: number | null | undefined): string {
  if (v == null || v === 0 || Number.isNaN(v)) return COLORS.textMuted
  return v > 0 ? COLORS.success : COLORS.danger
}

/** KPI card — white bg, border subtle, optional accent stripe left. */
export type KpiTone = 'neutral' | 'primary' | 'success' | 'danger' | 'warn'

export function kpiAccent(tone: KpiTone): string {
  switch (tone) {
    case 'primary': return COLORS.primary
    case 'success': return COLORS.success
    case 'danger': return COLORS.danger
    case 'warn': return COLORS.warn
    default: return COLORS.borderStrong
  }
}

/** Chip / pill — small neutral tag inside section headers. */
export const neutralChipSx: SxProps<Theme> = {
  height: 22,
  fontSize: '0.72rem',
  fontWeight: 600,
  bgcolor: COLORS.bgHeader,
  color: COLORS.textMuted,
  border: `1px solid ${COLORS.border}`,
}
